/**
 * RigStateButton - recall ONE look from a Rig State bank (PLAN.md QUEUED
 * "Rig States (visual recall)"). The button-shaped item kind the design
 * has been missing since Milestone 2's engine shipped (2026-08-15) - same
 * footing as FireButton, placeable on any page.
 *
 * Deliberately a single look per tile, not a bank-wide picker on the face -
 * see `RigStateButtonItem`'s doc comment in projectStore.ts for why.
 *
 * ## Dead state is loud, per DEC-124/138
 *
 * No bank, a bank with no look picked, or a look that's been deleted since
 * this tile was authored - all read the same as "cannot possibly fire" and
 * say so on the face, same convention as SnapshotChange/PresetChange's
 * pedal-removed state.
 *
 * ## Partial recall is reported, not swallowed
 *
 * `recallLook`'s `RecallOutcome[]` already carries per-target success/
 * failure (locked semantics: "surface a partial-recall warning"). This
 * component's whole feedback surface for that is one toast - a full recall
 * reads 'ok', anything less reads 'error' with the count, matching DEC-124's
 * "report what actually happened."
 */

import { createSignal } from 'solid-js';
import { editOwnsGesture, selectedItemId, type RigStateButtonItem, type LayoutColor } from '../layout/store.ts';
import { rigStateBanks, recallLook } from '../rigState/store.ts';
import { recallSummary } from '../rigState/targetWords.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface RigStateButtonProps {
  item: RigStateButtonItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function RigStateButton(props: RigStateButtonProps) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [firing, setFiring] = createSignal(false);

  const bank = () => {
    const id = props.item.bankId;
    return id ? rigStateBanks().find((b) => b.id === id) : undefined;
  };
  const look = () => {
    const b = bank();
    const id = props.item.lookId;
    return b && id ? b.looks.find((l) => l.id === id) : undefined;
  };
  /** A bankId with no lookId (or a look that no longer resolves) is just as
   *  dead as no bankId at all - neither can fire. */
  const dead = () => !props.item.bankId || !bank() || !props.item.lookId || !look();
  const live = () => !dead() && anyBridgeConnectedReactive(undefined);

  async function fire(): Promise<void> {
    const b = bank();
    const l = look();
    if (!b || !l) return;
    setFiring(true);
    try {
      const r = recallSummary(l.name, await recallLook(b.id, l.id));
      pushToast(r.tone, r.text, r.tone === 'ok' ? 1800 : r.tone === 'warn' ? 2500 : 5000);
    } finally {
      setFiring(false);
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return; // right-click escape hatch
    if (!live()) return;
    e.stopPropagation();
    if (!guard.gate('fire')) return; // guarded: first tap only arms
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel || dead();
  const faceText = () => props.item.name ?? look()?.name ?? 'RIG STATE';
  const subText = () => {
    if (!props.item.bankId) return 'no rig state assigned';
    if (!bank()) return 'rig state removed';
    if (!props.item.lookId) return 'no look picked';
    if (!look()) return 'look removed';
    if (!anyBridgeConnectedReactive(undefined)) return 'no bridge';
    if (firing()) return 'recalling...';
    return bank()!.name;
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-on="false"
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      onPointerDown={onPointerDown}
      disabled={!live()}
    >
      <PadDecor item={props.item} />
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && <span class="midi-button-label">{subText()}</span>}
    </button>
  );
}
